import React from 'react';
import { 
  Form, 
  Input, 
  Select, 
  Button,
  Space,
  Typography,
  Tag,
  Collapse,
  Alert
} from 'antd';
import { 
  TeamOutlined, 
  PlusOutlined, 
  MinusCircleOutlined,
  ApartmentOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;

/**
 * 👥 CMT Member Manager - ONBOARDING WIZARD STYLE
 * 
 * Exact replication of onboarding wizard member assignment
 * - Inline forms with Form.List
 * - Add Member buttons that create form fields directly
 * - No popups or modals
 */
export default function CMTMemberManager({ cmts, members, onMembersChange, form }) {

  // CMT Role options - MATCHING ONBOARDING EXACTLY
  const CMT_ROLES = [
    { value: 'cmt_leader', label: 'CMT Leader' },
    { value: 'cmt_leader_alt', label: 'CMT Leader (Alternate)' },
    { value: 'crisis_manager', label: 'Crisis Manager' },
    { value: 'safety_officer', label: 'Safety Officer' },
    { value: 'legal_compliance', label: 'Legal/Compliance' },
    { value: 'safety_security', label: 'Safety/Security' },
    { value: 'facilities', label: 'Facilities' },
    { value: 'talent_hr', label: 'Talent/HR' },
    { value: 'it_tech', label: 'IT/Tech' },
    { value: 'finance', label: 'Finance' },
    { value: 'client_service', label: 'Client Service' },
    { value: 'comms_media', label: 'Communications/Media' },
    { value: 'other', label: 'Other' }
  ];

  /**
   * 🎯 Render member assignment form for a specific CMT - EXACT ONBOARDING COPY
   */
  const renderCMTMemberForm = (cmtName, cmtId) => {
    return (
      <Form.List name={['cmtMembers', cmtId]}>
        {(fields, { add, remove }) => {
          return (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key} style={{ marginBottom: 16, padding: 12, border: '1px solid #f0f0f0', borderRadius: 4, backgroundColor: '#fafafa' }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <Text strong>Team Member #{key + 1}</Text>
                      <Button 
                        type="text" 
                        danger 
                        onClick={() => remove(name)} 
                        icon={<MinusCircleOutlined />}
                        size="small"
                      >
                        Remove
                      </Button>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'fullName']}
                        rules={[{ required: true, message: 'Please enter full name' }]}
                        style={{ flex: '1 1 250px', minWidth: '200px', marginBottom: '8px' }}
                      >
                        <Input placeholder="Full Name (e.g., John Smith)" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'cmtRole']}
                        rules={[{ required: true, message: 'Please select role' }]}
                        style={{ flex: '0 0 180px', minWidth: '150px', marginBottom: '8px' }}
                      >
                        <Select placeholder="Select Role">
                          {CMT_ROLES.map(role => (
                            <Option key={role.value} value={role.value}>
                              {role.label}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </div>

                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <Form.Item
                        {...restField}
                        name={[name, 'email']}
                        rules={[
                          { required: true, message: 'Please enter email' },
                          { type: 'email', message: 'Please enter valid email' }
                        ]}
                        style={{ flex: '1 1 250px', minWidth: '200px', marginBottom: '8px' }}
                      >
                        <Input placeholder="Email (e.g., john.smith@company.com)" />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        name={[name, 'phone']}
                        style={{ flex: '0 0 180px', minWidth: '150px', marginBottom: '8px' }}
                      >
                        <Input placeholder="Phone (e.g., +61 400 000 000)" />
                      </Form.Item>
                    </div>
                  </Space>
                </div>
              ))}

              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="dashed"
                  onClick={() => add()}
                  block
                  icon={<PlusOutlined />}
                  size="small"
                >
                  Add Member to {cmtName}
                </Button>
              </Form.Item>
            </>
          );
        }}
      </Form.List>
    );
  };

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <Title level={4} style={{ margin: 0 }}>
          <TeamOutlined style={{ marginRight: '8px' }} />
          CMT Members
        </Title>
        <Text type="secondary">Manage members for each crisis management team</Text>
      </div>

      {/* EXACT ONBOARDING WIZARD STYLE - FORM.LIST APPROACH */}
      {cmts && cmts.length > 0 ? (
        <Collapse 
          defaultActiveKey={cmts.map(cmt => cmt.id)} 
          ghost
        >
          {cmts.map((cmt) => (
            <Panel 
              key={cmt.id}
              header={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <ApartmentOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    <span style={{ fontWeight: 'bold' }}>{cmt.name}</span>
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '12px', 
                      color: '#666',
                      backgroundColor: '#f0f0f0',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {cmt.type}
                    </span>
                  </div>
                </div>
              }
            >
              {/* CMT Description */}
              {cmt.description && (
                <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                  <Text type="secondary" style={{ fontSize: '13px' }}>
                    <strong>Scope:</strong> {cmt.description}
                  </Text>
                </div>
              )}
              
              {/* ONBOARDING-STYLE INLINE FORM */}
              {renderCMTMemberForm(cmt.name, cmt.id)}
            </Panel>
          ))}
        </Collapse>
      ) : (
        <Alert
          message="No CMT Structure Found"
          description="This organization doesn't have any CMTs configured yet. Please configure the CMT structure first."
          type="info"
          showIcon
        />
      )}
    </div>
  );
}

